import { DateTime } from 'luxon';

import { MerchantAdCampaignResponse, VenueAdCampaignResponse } from 'apis/ad-bookings/types';
import { BasketDiscountType, CampaignType, WeeklySchedule } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { FeatureType } from 'common/types';

import { BudgetOption } from './ad-campaign/components/types';
import { API_UNLIMITED_MAX_DELIVERY_DISTANCE } from './components/budget-section/constants';
import { BasketDiscountWriteCampaignProps } from '../../apis/restaurant-api/merchant-admin/campaigns/api';

export type PaginationProps = {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  setPageNumber: (pageNumber: number) => void;
  setPageSize: (pageSize: number) => void;
};

export enum CampaignStatus {
  RUNNING = 'running',
  SCHEDULED = 'scheduled',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  STOPPED = 'stopped',
}

export type UsageType = 'indefinitely' | 'limited';

export enum ItemDiscountType {
  X_FOR_FREE = 'x_for_free',
  PERCENTAGE_OFF = 'percentage_off',
  AMOUNT_OFF = 'amount_off',
}

export type CampaignRulesFormValues = {
  minBasketSize?: number;
  firstPurchaseOnly: boolean;
  usageType?: UsageType;
  perUserUsageLimit?: number;
  isMinBasketIncreased?: boolean;
};

export type CampaignSchedulingFormValues = {
  startDate: string;
  endDate: string;
  weeklySchedule?: WeeklySchedule | null;
};

export type CommonCampaignFormValues = {
  campaignId?: string;
  agreementTermsAndConditions: boolean;
  totalBudgetSum: number;
  venueIds: string[];
} & CampaignRulesFormValues &
  CampaignSchedulingFormValues;

export type DeliveryDiscountFormValues = {
  campaignType: CampaignType.DELIVERY_DISCOUNT;
  maxDeliveryDistance: number;
} & CommonCampaignFormValues;

export type BasketDiscountFormValues = {
  campaignType: CampaignType.BASKET_DISCOUNT;
  discountType: BasketDiscountType;
  discountValue: BasketDiscountWriteCampaignProps['basketDiscount'];
  maxDeliveryDistance: typeof API_UNLIMITED_MAX_DELIVERY_DISTANCE;
} & CommonCampaignFormValues;

export type ItemDiscountConfigFormValues = {
  amount?: number;
  percentage?: number;
  freeItemsCount?: number;
  discountType: ItemDiscountType;
};

export type SingleItemDiscountItemFormValues = { itemId?: string };

export type ItemDiscountFormValues = {
  campaignType: CampaignType.ITEM_DISCOUNT;
  maxDeliveryDistance: typeof API_UNLIMITED_MAX_DELIVERY_DISTANCE;
  itemIds: string[];
  categoryIds: string[];
  itemTriggerCount?: number;
} & CommonCampaignFormValues &
  ItemDiscountConfigFormValues;

export type EndOfDayFormValues = Omit<ItemDiscountFormValues, 'campaignType'> & {
  endOfDayItemsCount?: number;
  campaignType: CampaignType.END_OF_DAY;
};

export type CampaignsFormValues =
  | DeliveryDiscountFormValues
  | BasketDiscountFormValues
  | ItemDiscountFormValues
  | EndOfDayFormValues;

export type CampaignsRoutesParams = {
  feature: FeatureType;
  slug: string;
  campaignId: string;
};

export type CampaignOption = {
  tag?: string;
  title: string;
  description: string;
  image: string;
  type: CampaignType;
};

export type AdCampaignScheduleStrategy = 'alwaysOn' | 'selectDates';

export type AdCampaignScheduleFormValues = {
  scheduleStrategy: AdCampaignScheduleStrategy;
  startDate: string;
  endDate?: string;
};

export type BidStrategy = 'auto' | 'custom';

export type AdCampaignBudgetType = 'low' | 'recommended' | 'high' | 'custom';

export type AdCampaignBudgetFormValues = {
  budgetAmount: number;
  budgetType: AdCampaignBudgetType;
  bidStrategy: BidStrategy;
  customBidAmount?: number;
};

export type VenueAdCampaignFormValues = {
  agreementTermsAndConditions: boolean;
  campaignId?: string;
  campaignType: CampaignType.ADS;
  isFreeTrial: boolean;
} & AdCampaignScheduleFormValues &
  AdCampaignBudgetFormValues;

export type MerchantAdCampaignFormValues = VenueAdCampaignFormValues & {
  venueIds: string[];
};

export type AdCampaignEditFormValues = AdCampaignFormValues & { campaignId: string };

export type AdsFreeTrialConfig = {
  formattedTotalBudgetWithCurrency: string;
  formattedWeeklyBudgetWithCurrency: string;
  durationWeeks: number;
  freeTrialDaysMinimum: number;
  freeTrialPlusPaidCampaignMinimumDays: number;
};

export type AdCampaignFormValues = VenueAdCampaignFormValues | MerchantAdCampaignFormValues;

export type VenueAdCampaignConfigFormValues = {
  currency: Currency;
  budgetOptions: BudgetOption[];
  estimatedReturnOnSpending: number | undefined | null;
  maximumCustomBidAmount: number;
  minimumBudgetAmount: number;
  maximumBudgetAmount: number;
  minimumBidAmount: number;
  freeTrialBudgetAmount: number;
  freeTrialDurationWeeks: number;
};

export type MerchantAdCampaignConfigFormValues = {
  currency: Currency;
  budgetOptions: BudgetOption[];
  estimatedReturnOnSpending: number | undefined | null;
  maximumCustomBidAmount: number;
  minimumBudgetAmount: number;
  maximumBudgetAmount: number;
  minimumBidAmount: number;
};

export type AdCampaignConfigFormValues = VenueAdCampaignConfigFormValues | MerchantAdCampaignConfigFormValues;

export type VenueAdCampaign = Omit<VenueAdCampaignResponse, 'status'> & {
  status: CampaignStatus;
  campaign_type: CampaignType.ADS;
};

export type MerchantAdCampaign = Omit<MerchantAdCampaignResponse, 'status'> & {
  status: CampaignStatus;
  campaign_type: CampaignType.ADS;
};

export type CampaignCreationSource = 'merchantAdmin' | 'opsTools';

export type CampaignPaidBy = 'WOLT' | 'MERCHANT' | 'SHARED';

export type CampaignAction = 'clone' | 'edit' | 'delete' | 'stop' | 'restart' | 'terminate' | 'read';

export type CampaignListItem = {
  campaignId: string;
  campaignType: CampaignType;
  isAdFreeTrial?: boolean;
  startDate: string;
  endDate?: string;
  status: CampaignStatus;
  budget: number;
  allowedActions: CampaignAction[];
  paidBy: CampaignPaidBy;
  creationSource?: CampaignCreationSource;
  isCreatedByWolt?: boolean;
  isBudgetExceeded?: boolean;
  venueIds: string[];
};

export type VenuesValidationError = { venueIds: string[]; reason: string };

export type AdCampaign = VenueAdCampaign | MerchantAdCampaign;

export type DateRange = {
  from: DateTime;
  to: DateTime;
};
